import type { RefObject } from 'react';
import { ScrollArea } from '@/shared/components/ui/scroll-area';
import type { Message } from '../../types';
import { MessageBubble } from './MessageBubble';
import { TypingIndicator } from './TypingIndicator';
import { getMessageKey } from './getMessageKey';

type MessageListProps = {
  messages: Message[];
  selfWorkspaceId?: string;
  currentUserId?: string;
  participantName: string;
  participantPhoto: string | null;
  isParticipantTyping: boolean;
  typingName: string;
  scrollAreaRef: RefObject<HTMLDivElement | null>;
  messagesEndRef: RefObject<HTMLDivElement | null>;
};

/** Extracted from `ChatInterface` to stay under the 300-line file limit. */
export const MessageList = ({
  messages,
  selfWorkspaceId,
  currentUserId,
  participantName,
  participantPhoto,
  isParticipantTyping,
  typingName,
  scrollAreaRef,
  messagesEndRef,
}: MessageListProps) => (
  <ScrollArea className="flex-1 p-4" ref={scrollAreaRef}>
    {messages.length === 0 ? (
      <div className="flex items-center justify-center h-full text-center">
        <div>
          <p className="text-gray-600 mb-2">No messages yet</p>
          <p className="text-sm text-gray-500">Start the conversation!</p>
        </div>
      </div>
    ) : (
      <div>
        {messages.map((message, index) => {
          const isOwnMessage = message.senderId === selfWorkspaceId;
          const previousMessage = index > 0 ? messages[index - 1] : null;
          // Group by sender AND the actual typist, so two delegates in the
          // same workspace each get their own avatar + attribution run, but
          // consecutive messages from the same author collapse.
          const sameRun =
            previousMessage !== null &&
            previousMessage.senderId === message.senderId &&
            (previousMessage.performedByUserId ?? null) === (message.performedByUserId ?? null);

          return (
            <MessageBubble
              key={getMessageKey(message, index)}
              message={message}
              isOwnMessage={isOwnMessage}
              showAvatar={!sameRun}
              showSenderName={!sameRun}
              currentUserId={currentUserId}
              participantName={participantName}
              participantPhoto={participantPhoto}
            />
          );
        })}

        {isParticipantTyping && <TypingIndicator participantName={typingName} />}

        <div ref={messagesEndRef} />
      </div>
    )}
  </ScrollArea>
);
